/** @jsxImportSource @emotion/react */
import React, { useCallback } from 'react'
import { SwapOutlined } from '@ant-design/icons'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import {
  HomeTeam,
  VisitingTeam,
} from '@sportninja/common/types/PlayerAttendance'
import {
  snacksContainerStyle,
  snacksImageStyle,
  snacksLabelAndTextContainerStyle,
  snacksLabelStyle,
  snacksTextStyle,
  styledSwapOutlinedStyle,
} from '../styles'
import { trackEvent, getPlatformType } from '../../../utils/analytics'

const PLACEHOLDER_PLAYER = '/images/generic_placeholders/placeholder_player.png'

/**
 * Props for the SnacksInfo component
 */
export interface SnacksInfoProps {
  /** Whether the current user is a team rep on this team */
  isTeamRepOnTeam: boolean
  /** Team data containing refreshments assignments */
  team: HomeTeam | VisitingTeam
  /** Team name suffix shown when user is on both teams (e.g., "(Team Name)") */
  teamNameSuffix: string
  /** Callback when manage refreshments is pressed */
  onPressManageRefreshments: (
    gameId: string,
    rosterId: string,
    teamId: string,
    teamName: string,
    showWaiver: boolean
  ) => void
  /** Game ID */
  gameId: string
  /** Whether to show waiver */
  showWaiver: boolean
  /** Which side of the game this team is on */
  teamSide: 'home' | 'visitor'
}

/**
 * Displays the drinks/snacks assignment for a team.
 * Shows the assigned person's avatar and name, or a prompt to choose someone.
 * Team reps can click to manage the refreshments assignment.
 *
 * @param {SnacksInfoProps} props - Component props
 * @returns {JSX.Element} Snacks/refreshments info display
 */
const SnacksInfo: React.FC<SnacksInfoProps> = ({
  isTeamRepOnTeam,
  team,
  teamNameSuffix,
  onPressManageRefreshments,
  gameId,
  showWaiver,
  teamSide,
}) => {
  const handleClick = useCallback(() => {
    if (!isTeamRepOnTeam) {
      return
    }
    const hasAssignment = !!(
      team?.refreshments_player?.name_full ||
      team?.refreshments_team_official?.name_full
    )
    trackEvent('home_game_card_refreshments_tapped', {
      platform: getPlatformType(),
      game_id: gameId,
      team_side: teamSide,
      has_assignment: hasAssignment,
    })
    onPressManageRefreshments(
      gameId,
      team?.game_roster_id,
      team?.id,
      team?.name,
      showWaiver
    )
  }, [
    isTeamRepOnTeam,
    onPressManageRefreshments,
    gameId,
    team,
    showWaiver,
    teamSide,
  ])

  const imageSrc =
    getImageThumbnailId(team?.refreshments_player) ||
    getImageThumbnailId(team?.refreshments_team_official) ||
    PLACEHOLDER_PLAYER

  const assignedName = team?.refreshments_player?.name_full
    ? team?.refreshments_player?.name_full
    : team?.refreshments_team_official?.name_full
    ? team?.refreshments_team_official?.name_full
    : isTeamRepOnTeam
    ? 'Choose a Person'
    : 'Not Set'

  return (
    <div css={snacksContainerStyle(isTeamRepOnTeam)} onClick={handleClick}>
      <img
        css={snacksImageStyle}
        src={imageSrc}
        alt='Snacks'
        onError={(e) => {
          e.currentTarget.src = PLACEHOLDER_PLAYER
        }}
      />
      <div css={snacksLabelAndTextContainerStyle}>
        <p css={snacksLabelStyle}>Drinks/Snacks {teamNameSuffix}</p>
        <p css={snacksTextStyle}>{assignedName}</p>
      </div>
      {isTeamRepOnTeam ? <SwapOutlined css={styledSwapOutlinedStyle} /> : null}
    </div>
  )
}

export default React.memo(SnacksInfo)
